
import React, { useState } from 'react';
import { X, ShoppingCart, Heart, Share2, Star, MessageSquare, Send } from 'lucide-react';
import '../../styles/marketplace.css';
import { marketplaceAPI } from '../../services/marketplaceAPI';

const ProductDetailsModal = ({ product, isOpen, onClose, onAddToCart, onViewProvider, selectedCurrency, rates }) => {
    if (!isOpen || !product) return null;

    const [activeTab, setActiveTab] = useState('details');
    const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
    const [submitting, setSubmitting] = useState(false);
    // Local state to show immediately added reviews without refetching parent
    // Use spread to create a copy, preventing mutation issues with the mock API shared reference
    const [localReviews, setLocalReviews] = useState(product.reviewsData ? [...product.reviewsData] : []);

    // Reset state when product changes
    React.useEffect(() => {
        if (isOpen && product) {
            setActiveTab('details');
            setLocalReviews(product.reviewsData ? [...product.reviewsData] : []);
            setReviewForm({ rating: 5, comment: '' });
        }
    }, [isOpen, product]);

    const { title, price, currency: baseCurrency, image, description, rating, reviews, providerName, providerId, stock, category } = product;

    // Currency Conversion
    const displayCurrency = selectedCurrency || baseCurrency;
    const rate = rates && rates[displayCurrency] ? rates[displayCurrency] : 1;
    const displayPrice = (price * rate).toFixed(2);

    const renderStars = (rating) => {
        return [...Array(5)].map((_, i) => (
            <Star
                key={i}
                size={16}
                fill={i < Math.floor(rating) ? "currentColor" : "none"}
                className={`mp - star ${i < Math.floor(rating) ? 'filled' : ''} `}
            />
        ));
    };

    const handleSubmitReview = async () => {
        if (!reviewForm.comment.trim()) return;
        setSubmitting(true);
        try {
            const res = await marketplaceAPI.addReview(product.id, reviewForm);
            if (res.status === 'success') {
                setLocalReviews([res.data, ...localReviews]);
                setReviewForm({ rating: 5, comment: '' });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mp-modal-overlay" onClick={onClose}>
            <div className="mp-modal-content" style={{ maxWidth: '900px', display: 'flex', flexDirection: 'column', padding: 0, position: 'relative' }} onClick={e => e.stopPropagation()}>

                {/* Close Button - Absolute */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: 'rgba(255,255,255,0.8)',
                        borderRadius: '50%',
                        padding: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        zIndex: 10
                    }}
                >
                    <X size={24} />
                </button>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '500px' }} className="mp-details-grid">

                    {/* Left: Image Side */}
                    <div style={{ background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                        <img
                            src={image}
                            alt={title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', maxHeight: '600px' }}
                        />
                    </div>

                    {/* Right: Info Side */}
                    <div style={{ padding: '40px', display: 'flex', flexDirection: 'column' }}>


                        {/* Tabs Header */}
                        <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', marginBottom: '24px' }}>
                            <button
                                onClick={() => setActiveTab('details')}
                                style={{
                                    padding: '12px 24px',
                                    border: 'none',
                                    background: 'none',
                                    borderBottom: activeTab === 'details' ? '2px solid #8548A8' : '2px solid transparent',
                                    color: activeTab === 'details' ? '#8548A8' : '#6B7280',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Details
                            </button>
                            <button
                                onClick={() => setActiveTab('reviews')}
                                style={{
                                    padding: '12px 24px',
                                    border: 'none',
                                    background: 'none',
                                    borderBottom: activeTab === 'reviews' ? '2px solid #8548A8' : '2px solid transparent',
                                    color: activeTab === 'reviews' ? '#8548A8' : '#6B7280',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Reviews ({localReviews.length})
                            </button>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'details' ? (
                            <>
                                <div style={{ marginBottom: '8px', textTransform: 'uppercase', fontSize: '12px', fontWeight: 'bold', color: '#8548A8', letterSpacing: '1px' }}>
                                    {category} • <span
                                        onClick={() => onViewProvider && onViewProvider(providerId)}
                                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                        title="View Seller Profile"
                                    >
                                        {providerName}
                                    </span>
                                </div>

                                <h2 style={{ fontSize: '32px', fontWeight: '800', margin: '0 0 16px 0', lineHeight: 1.2 }}>{title}</h2>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                                    <div style={{ display: 'flex' }}>{renderStars(rating)}</div>
                                    <span style={{ color: '#6B7280', fontSize: '14px' }}>({localReviews.length} reviews)</span>
                                </div>

                                <div style={{ fontSize: '32px', fontWeight: '800', color: '#8548A8', marginBottom: '24px' }}>
                                    <span style={{ fontSize: '16px', color: '#6B7280', fontWeight: '400', marginRight: '4px' }}>{displayCurrency}</span>
                                    {displayPrice}
                                </div>

                                <div style={{ borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', padding: '24px 0', marginBottom: '24px' }}>
                                    <p style={{ color: '#4B5563', lineHeight: 1.6, margin: 0 }}>
                                        {description || "No description available for this product."}
                                    </p>
                                </div>

                                {/* Actions */}
                                <div style={{ marginTop: 'auto', display: 'flex', gap: '16px' }}>
                                    <button
                                        onClick={() => {
                                            onAddToCart(product);
                                            onClose();
                                        }}
                                        disabled={stock === 0}
                                        className="mp-btn-primary"
                                        style={{
                                            flex: 1,
                                            justifyContent: 'center',
                                            padding: '16px',
                                            fontSize: '16px',
                                            opacity: stock === 0 ? 0.5 : 1,
                                            cursor: stock === 0 ? 'not-allowed' : 'pointer',
                                            background: stock === 0 ? '#9CA3AF' : 'var(--mp-primary)'
                                        }}
                                    >
                                        <ShoppingCart size={20} />
                                        {stock === 0 ? 'Out of Stock' : 'Add to Cart'}
                                    </button>

                                    <button style={{
                                        width: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: '1px solid #E5E7EB', borderRadius: '12px', background: 'white', cursor: 'pointer'
                                    }}>
                                        <Heart size={24} color="#4B5563" />
                                    </button>
                                </div>

                                <div style={{ marginTop: '24px', fontSize: '13px', color: '#6B7280', display: 'flex', gap: '16px' }}>
                                    <span>Availability:
                                        {stock > 10 ? <span style={{ color: '#10B981', fontWeight: 'bold', marginLeft: '4px' }}>In Stock</span> :
                                            stock > 0 ? <span style={{ color: '#F59E0B', fontWeight: 'bold', marginLeft: '4px' }}>Low Stock ({stock})</span> :
                                                <span style={{ color: '#EF4444', fontWeight: 'bold', marginLeft: '4px' }}>Out of Stock</span>}
                                    </span>
                                    <span>•</span>
                                    <span>Free Shipping Eligible</span>
                                </div>
                            </>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                {/* Review List container */}
                                <div style={{ flex: 1, overflowY: 'auto', paddingRight: '10px', marginBottom: '20px' }}>
                                    {localReviews.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
                                            <MessageSquare size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
                                            <p>No reviews yet. Be the first to share your thoughts!</p>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                            {localReviews.map(review => (
                                                <div key={review.id} style={{ borderBottom: '1px solid #F3F4F6', paddingBottom: '16px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{review.user}</span>
                                                        <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{review.date}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', marginBottom: '8px', color: '#FBBF24' }}>
                                                        {[...Array(5)].map((_, i) => (
                                                            <Star key={i} size={14} fill={i < review.rating ? "currentColor" : "none"} stroke="none" />
                                                        ))}
                                                    </div>
                                                    <p style={{ margin: 0, fontSize: '14px', color: '#4B5563', lineHeight: 1.5 }}>
                                                        {review.comment}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Write Review Form */}
                                <div style={{ background: '#F9FAFB', padding: '20px', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>Write a Review</h4>

                                    <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: star <= reviewForm.rating ? '#FBBF24' : '#D1D5DB' }}
                                            >
                                                <Star size={20} fill={star <= reviewForm.rating ? "currentColor" : "none"} />
                                            </button>
                                        ))}
                                    </div>

                                    <textarea
                                        value={reviewForm.comment}
                                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                                        placeholder="Share your thoughts about this product..."
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: '1px solid #D1D5DB',
                                            marginBottom: '12px',
                                            minHeight: '80px',
                                            fontSize: '14px',
                                            resize: 'vertical'
                                        }}
                                    />

                                    <button
                                        onClick={handleSubmitReview}
                                        disabled={submitting || !reviewForm.comment.trim()}
                                        className="mp-btn-primary"
                                        style={{ width: '100%', padding: '10px', fontSize: '14px', justifyContent: 'center' }}
                                    >
                                        {submitting ? 'Submitting...' : 'Post Review'}
                                        {!submitting && <Send size={14} style={{ marginLeft: '8px' }} />}
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailsModal;
